import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { deleteAccountAndClearDevice, refreshAccount, signOutAndClearDevice } from "../engine/signin";
import { useApp } from "../state/store";

import {
  Button,
  Card,
  CheckIcon,
  Overlay,
  ScreenHeading,
  SkeletonCard,
  SkeletonGate,
  SkeletonLine,
  StatusLine,
  SubHeading,
  WhoCard,
  cx,
} from "../ui";
import { TransactionRow, TransactionRowSkeleton } from "./credits-history";
import { DeleteConsequences, STORE_SUBSCRIPTIONS_URL, type PlanOnDelete } from "./delete-account";
import { PlansOverlay } from "./plan-picker";
import { Screen } from "./screen";
import { TopUpOverlay } from "./top-up";
import { sayOn, useScreenStatus } from "./use-screen-status";
import {
  balanceLine,
  billingState,
  credits,
  planCard,
  planCardLine,
  planDate,
  planEnding,
  planName,
  plansOn,
  planStore,
  topUpSheet,
  useWallet,
  type CardTone,
  type PlanKey,
  type Wallet,
} from "./wallet";

/** Account's two billing overlays (#1010): the plans, and Add credits' packs. */
type Sheet = "plans" | "packs";

/** How many of the latest history rows Account shows before See all. */
export const HISTORY_PREVIEW = 2;

/** How often, and how many times, to look for a top-up after Dodo hands the host back. */
const RETURN_POLL_MS = 2500;
const RETURN_POLL_TRIES = 8;

/** Whether the wallet shows a top-up that landed after `since`. */
function topUpSince(wallet: Wallet | null, since: number): boolean {
  return !!wallet?.transactions.some(
    (tx) => tx.type === "topup" && new Date(tx.created_at).getTime() >= since
  );
}

/**
 * What Delete account's confirm step says about a plan (#887), from the wallet: a web plan
 * that would charge again (the deletion cancels it), and the server's `store_plan`. Without
 * a wallet it can't tell, so it says the general line about store plans.
 */
export function planOnDelete(wallet: Wallet | null): PlanOnDelete {
  if (!wallet) return "unknown";
  const sub = wallet.subscription ?? null;
  return {
    web: sub?.provider === "dodo" && sub.status !== "expired",
    store: wallet.store_plan ?? null,
  };
}

/** Whether a plan query value is one of the plans, as `/account?plan=` carries it back. */
function isPlanKey(value: string | null): value is PlanKey {
  return value === "basic" || value === "plus" || value === "pro";
}

/**
 * The signed-in account: who it is, its plan and credits, and the two things it can do
 * here -- set up its voice again, or leave. The session history lives on its own screen now
 * (`/sessions`, linked from home).
 *
 * The voice profile is the small accent button in the identity card: its check says a
 * profile is already on this device, and pressing it re-records. The separate Your voice,
 * Your privacy and Meeting data blocks are gone, and removing them leaves the billing
 * section, the history and one quiet way out.
 *
 * Signing in is optional and stays optional. Recording, joining and reading a transcript
 * all run on the device token; an account is what makes history and a lost device
 * survivable. The copy says so rather than implying a sign-in wall.
 *
 * ClaimProfile rows and signed-in browsers are deliberately not listed here. A device's
 * identity is its own, the rows accumulate and cannot be deleted (they are the
 * evidence + history anchor), and surfacing them on the account screen read as clutter
 * with no safe action -- revoking the device in your hand locks you out of the screen
 * you would need to undo it. The top Sign out revokes *this* device and clears it; past
 * device rows stay on the server as evidence and are no longer shown.
 */
export function Account({ plans = false }: { plans?: boolean } = {}) {
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const template = useApp((s) => s.template);
  const link = useApp((s) => s.deviceAccountLink);
  const [status, say] = useScreenStatus();
  const { wallet, failed: walletFailed, reload: reloadWallet } = useWallet(!!account);
  /** Retry after a failed read: loading again, so the skeleton's rules apply to it too. */
  const [walletRetrying, setWalletRetrying] = useState(false);
  async function retryWallet() {
    setWalletRetrying(true);
    await reloadWallet();
    setWalletRetrying(false);
  }
  const [params, setParams] = useSearchParams();
  /**
   * The overlay asked for: the plans, Add credits, or `top_up` -- Add credits asked for by
   * `/account?top_up=1`, which shipped builds still link to and which becomes the plans or
   * the packs once the wallet says which (`topUpSheet`, #1010).
   */
  const [asked, setAsked] = useState<Sheet | "top_up" | null>(() =>
    plans ? "plans" : params.has("top_up") ? "top_up" : null
  );
  const sheet: Sheet | null = asked === "top_up" ? (wallet ? topUpSheet(wallet) : null) : asked;
  const [deleting, setDeleting] = useState(false);
  // Overlays are counted without counting the page beneath them again (#1085): the plans
  // are a screen of their own; top-up is a dialog.
  const previousSheet = useRef<Sheet | null>(null);
  const plansCounted = useRef(false);
  useEffect(() => {
    const previous = previousSheet.current;
    previousSheet.current = sheet;
    if (sheet === "plans") {
      if (!plansCounted.current) {
        plansCounted.current = true;
        // The `/plans` route already emitted its screen view; the inline overlay has not.
        if (!plans) captureAnalytics("screen_viewed", { screen: "plans" });
      }
    } else {
      plansCounted.current = false;
    }
    if (sheet === "packs" && previous !== "packs") {
      captureAnalytics("dialog_viewed", { dialog: "top_up" });
    } else if (previous === "packs" && sheet !== "packs") {
      captureAnalytics("dialog_dismissed", { dialog: "top_up" });
    }
  }, [sheet, plans]);
  /** The query this visit arrived with, read once: the effect below strips it. */
  const arrival = useRef({
    status: params.get("status"),
    paid: params.has("payment_id") || params.has("subscription_id"),
    plan: params.get("plan"),
  });

  /**
   * Wait for a plan to show in the wallet (#875): after a plan's checkout, or an upgrade
   * Dodo is charging for. "Being confirmed" until it does, then "Your {plan} plan is
   * active." Returns the poll's stop.
   */
  function awaitPlan(plan: PlanKey): () => void {
    say(t("plans.purchase_confirming"));
    let tries = 0;
    const timer = window.setInterval(async () => {
      tries += 1;
      const next = await reloadWallet();
      if (next?.plan === plan) {
        say(t("plans.purchase_active", { plan: planName(plan) }), "ok");
        window.clearInterval(timer);
      } else if (tries >= RETURN_POLL_TRIES) {
        window.clearInterval(timer);
      }
    }, RETURN_POLL_MS);
    return () => window.clearInterval(timer);
  }

  // Back from Dodo's checkout (its return_url is /account, with `payment_id`, `status` and
  // sometimes `email` added), or sent here to top up. The query is consumed once and removed, so a reload
  // or a Back does not replay it. A payment's credit arrives by webhook, usually within
  // seconds of the return and sometimes just before it, so the balance is read again a
  // few times until the top-up shows -- and the copy says "in a moment" until it does.
  useEffect(() => {
    const { status: returned, paid, plan } = arrival.current;
    if (params.has("top_up") || paid || returned || plan) setParams({}, { replace: true });
    if (!paid && !returned) return;
    // Dodo's `status`: `succeeded`, `failed`, `expired`, or one that settles later
    // (`processing`, `requires_*`) -- and it can be missing. Only `succeeded` is said to be
    // a payment; anything unknown is "being confirmed", never "received".
    if (returned === "failed" || returned === "expired") {
      say(t("billing.returned_unpaid"), "err");
      return;
    }
    // Back from a plan's checkout (#875): the plan arrives by webhook, as a top-up does.
    if (isPlanKey(plan)) return awaitPlan(plan);
    say(returned === "succeeded" ? t("billing.returned_paid") : t("billing.returned_pending"));
    // A top-up from this payment can only be newer than this, and an older one is not it.
    const since = Date.now() - 30 * 60 * 1000;
    let tries = 0;
    const timer = window.setInterval(async () => {
      tries += 1;
      const next = await reloadWallet();
      if (topUpSince(next, since)) {
        say(t("billing.returned_added"), "ok");
        window.clearInterval(timer);
      } else if (tries >= RETURN_POLL_TRIES) {
        window.clearInterval(timer);
      }
    }, RETURN_POLL_MS);
    return () => window.clearInterval(timer);
    // Once per visit: `arrival` is this visit's query, and the rest are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Asked to top up while already here: the app's Add credits handoff signs in on /account
  // and only then asks for the top-up (#689). Opened, and the query removed, as above.
  useEffect(() => {
    if (!params.has("top_up")) return;
    setAsked("top_up");
    setParams({}, { replace: true });
  }, [params, setParams]);

  /**
   * Close the overlay. Opened by `/plans` (Home's Choose a plan), it goes back where it came
   * from, or to Account when it was the first page.
   */
  function closeSheet() {
    setAsked(null);
    if (!plans) return;
    if ((window.history.state as { idx?: number } | null)?.idx) navigate(-1);
    else navigate("/account", { replace: true });
  }

  return (
    <Screen name="account">
      <ScreenHeading>{t("account.heading")}</ScreenHeading>

      {(link.status === "error" || link.status === "conflict") && (
        <div className="my-4 rounded-panel border border-line bg-surface-2 p-pad">
          <p className="m-0 text-lede">
            {t("account.link_error")}
          </p>
          <Button variant="link" onClick={() => {
            captureAnalytics("retry_tapped", { surface: "account_link" });
            void refreshAccount();
          }}>
            {t("common.retry")}
          </Button>
        </div>
      )}

      <WhoCard
        name={account?.email}
        nameClassName="wrap-anywhere"
        detail={
          <span className="mt-5 flex">
            <Button
              size="small"
              onClick={() => {
                navigate("/rerecord");
              }}
            >
              <span className="inline-flex items-center justify-center gap-tight">
                {t("account.voice_profile")}
                {template ? <CheckIcon className="size-icon-xs" /> : null}
              </span>
            </Button>
          </span>
        }
        action={
          <Button
            variant="link"
            className="flex-none whitespace-nowrap"
            onClick={async () => {
              if (
                !window.confirm(t("account.sign_out_confirm"))
              )
                return;
              await signOutAndClearDevice();
              captureAnalytics("account_signed_out", {});
              navigate("/signin");
            }}
          >
            {t("account.sign_out")}
          </Button>
        }
      />

      {/* Your plan (#1010), or Credits with plans off (#686): the plan card, the balance as
          a plain credit count, never dollars -- see `wallet.ts` -- and the last few things
          that moved it. Under the identity card: it is what a host comes back to this
          screen for. */}
      {/* Loading, the whole section is one skeleton (#1043): its heading is held too,
          because which heading it is depends on the wallet. The card family has about ten
          layouts, so the card is one neutral block (rule 7), and the real one fades in. The
          history under it has one layout, so it is drawn as it will be: the label, See all,
          and the preview's rows (#1049). */}
      <SkeletonGate
        loading={!wallet && (!walletFailed || walletRetrying)}
        label={t("billing.loading")}
        skeleton={
          <>
            <SubHeading>
              <SkeletonLine size="label" width="w-tile-sm" />
            </SubHeading>
            <SkeletonCard className="my-4" />
            <div className="flex items-center justify-between my-6">
              <SkeletonLine size="label" width="w-tile-lg" />
              <SkeletonLine size="link" width="w-tile-xs" className="p-link pr-1" />
            </div>
            <div className="grid gap-2 mb-4">
              {Array.from({ length: HISTORY_PREVIEW }, (_, i) => (
                <TransactionRowSkeleton key={i} />
              ))}
            </div>
          </>
        }
      >
        <SubHeading>{wallet && plansOn(wallet) ? t("plans.current") : t("billing.heading")}</SubHeading>
        {wallet ? (
          <WalletSection
            wallet={wallet}
            onOpen={setAsked}
            onManageFailed={() => say(t("plans.change_failed"), "err")}
          />
        ) : (
          <div className="my-4">
            <p className="m-0 text-note text-danger">{t("billing.load_error")}</p>
            <Button variant="link" onClick={() => void retryWallet()}>
              {t("common.retry")}
            </Button>
          </div>
        )}
      </SkeletonGate>

      {/* Deleting the account (#781): last, below everything it would take with it, a step
          of its own -- the overlay says what goes before anything does. A quiet red caption,
          centred, with the static Privacy · Terms · Support footer below it (#1250). */}
      <hr />
      <div className="mt-4 text-center">
        <Button variant="link" danger size="small" onClick={() => {
          captureAnalytics("account_delete_started", {});
          captureAnalytics("dialog_viewed", { dialog: "delete_confirm" });
          setDeleting(true);
        }}>
          {t("account.delete_row")}
        </Button>
      </div>

      <StatusLine kind={status.kind}>{status.message}</StatusLine>

      {sheet === "plans" && wallet ? (
        <PlansOverlay
          wallet={wallet}
          onClose={closeSheet}
          onPlanChanged={(plan, effective) => {
            setAsked(null);
            // An upgrade shows once Dodo has charged for it; a switch waits for the renewal
            // and the plan card says when.
            if (effective === "now") awaitPlan(plan);
            else void reloadWallet();
          }}
        />
      ) : null}
      {sheet === "packs" && wallet ? (
        <TopUpOverlay
          wallet={wallet}
          onClose={closeSheet}
          onComparePlans={() => setAsked("plans")}
          // Refused for want of a plan (#1009): the wallet was older than the plan's end.
          onPlanRequired={() => {
            setAsked("plans");
            void reloadWallet();
          }}
        />
      ) : null}
      {deleting ? (
        <DeleteAccountOverlay plan={planOnDelete(wallet)} onClose={() => setDeleting(false)} />
      ) : null}
    </Screen>
  );
}

/**
 * The confirm step for Delete account (#781): what goes, that it cannot be undone, and the
 * one destructive button. No grace period, so nothing is deleted until that tap.
 *
 * On success this device is wiped as signing out wipes it -- voice profile and keys -- and
 * sign-in says the account is gone. A meeting still going or saving is refused by the
 * server and said here in plain words; the account is untouched.
 *
 * A plan (#887): one bought here is cancelled by the deletion, and the list says so; one
 * from the App Store or Google Play keeps charging, so it is named, with where to cancel.
 */
function DeleteAccountOverlay({ plan, onClose }: { plan: PlanOnDelete; onClose: () => void }) {
  const navigate = useNavigate();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  function close() {
    captureAnalytics("dialog_dismissed", { dialog: "delete_confirm" });
    onClose();
  }

  async function confirm() {
    setWorking(true);
    setError("");
    try {
      await deleteAccountAndClearDevice();
      captureAnalytics("account_deleted", {});
      sayOn("/signin", t("account.deleted"), "ok");
      navigate("/signin");
    } catch (err) {
      const code = (err as { data?: { code?: string } }).data?.code;
      setError(code === "account_in_live_meeting" ? t("account.delete_busy") : t("account.delete_failed"));
      setWorking(false);
    }
  }

  return (
    <Overlay
      label={<span className="uppercase">{t("account.delete_row")}</span>}
      aria-label={t("account.delete_heading")}
      onClose={close}
    >
      <ScreenHeading>{t("account.delete_heading")}</ScreenHeading>
      <DeleteConsequences plan={plan} />
      <Button danger className="w-full mt-pad" disabled={working} onClick={() => void confirm()}>
        {working ? t("account.delete_working") : t("account.delete_confirm")}
      </Button>
      <Button variant="ghost" className="w-full mt-gutter" disabled={working} onClick={close}>
        {t("common.cancel")}
      </Button>
      <StatusLine kind={error ? "err" : ""}>{error}</StatusLine>
    </Overlay>
  );
}

/** The card's tone as text and bar colours. */
const TONE_TEXT: Record<CardTone, string> = { ink: "text-ink", warn: "text-warn-ink", danger: "text-danger" };

/**
 * The plan card (#1010, option A of #1024), first under Your plan while plans are on, from
 * `billingState` and `planCard`, top to bottom:
 *
 * 1. the plan's name (Basic, Plus, Pro, or Free trial), with no price;
 * 2. "{left} of {total} monthly credits left" (or "trial credits"), in the card's tone;
 * 3. a bar of left against total, in the same tone;
 * 4. "Resets on {date}", or "Ends on {date}" for the trial and a plan that is ending;
 * 5. "{n} extra credits" with "Never expire", only when there are any;
 * 6. the state line: Running low, Out of credits, or a failed payment;
 * 7. the primary button: Buy credits running low or out, Update payment details after a
 *    failed payment, See plans on the trial;
 * 8. the links: Change plan and Manage billing; Change plan after a failed payment; Keep
 *    my plan when ending; none on the trial. A plan billed in a store says "Manage in App
 *    Store" or "Manage in Google Play" for Manage billing (and Keep my plan), linking to
 *    that store's subscriptions.
 *
 * Buy credits shows only when credits are needed (#1038), and not at all where checkout
 * can't sell them: never a disabled button or an "isn't available" line on this card.
 */
export function PlanCard({
  wallet,
  onOpen,
  onManageFailed,
}: {
  wallet: Wallet;
  onOpen: (sheet: Sheet) => void;
  onManageFailed: () => void;
}) {
  const sub = wallet.subscription ?? null;
  const state = billingState(wallet);
  const card = planCard(wallet, state);
  const [opening, setOpening] = useState(false);
  const store = planStore(wallet);
  const failed = state === "payment_failed" || state === "payment_paused";
  const short = state === "plan_low" || state === "plan_out";
  const ending = planEnding(wallet);
  const line = planCardLine(card);
  const stateLine =
    state === "plan_low" || state === "trial_low"
      ? t("plans.running_low")
      : state === "plan_out"
        ? t("plans.out")
        : state === "payment_failed"
          ? t("plans.payment_failed_active")
          : state === "payment_paused"
            ? t("plans.payment_failed_paused")
            : null;
  const pending =
    sub?.pending_plan && sub.current_period_end
      ? t("plans.switches_on", { plan: planName(sub.pending_plan), date: planDate(sub.pending_plan_at ?? sub.current_period_end) })
      : null;

  async function portal() {
    captureAnalytics("subscription_manage_opened", {});
    setOpening(true);
    try {
      const { url } = await api.subscriptionPortal();
      window.location.assign(url);
    } catch (err) {
      console.warn("portal failed", err);
      onManageFailed();
      setOpening(false);
    }
  }

  /**
   * A billing link: Dodo's portal for a web plan, the store's subscriptions for a store's.
   * Manage billing names the store; Keep my plan says the same wherever it is billed.
   */
  function billingLink(label: string) {
    return store ? (
      <a
        href={STORE_SUBSCRIPTIONS_URL[store]}
        target="_blank"
        rel="noreferrer"
        onClick={() => {
          captureAnalytics("subscription_manage_opened", {});
          captureAnalytics("link_opened", { link: "store_subscriptions" });
        }}
        className="p-link text-link font-medium text-ink-soft underline underline-offset-3"
      >
        {label}
      </a>
    ) : (
      <Button variant="link" disabled={opening} onClick={() => void portal()}>
        {label}
      </Button>
    );
  }
  const manageLabel = store
    ? store === "apple_iap"
      ? t("plans.manage_app_store")
      : t("plans.manage_google_play")
    : t("plans.manage_billing");
  const changePlan = (
    <Button variant="link" onClick={() => onOpen("plans")}>
      {t("plans.change")}
    </Button>
  );
  const links = card.trial ? null : failed ? (
    changePlan
  ) : ending ? (
    billingLink(t("plans.keep"))
  ) : (
    <>
      {changePlan}
      {billingLink(manageLabel)}
    </>
  );

  return (
    <Card data-plan-card data-billing-state={state} className="flex flex-col gap-stack">
      <span className="font-title text-person font-semibold tracking-headline text-ink">
        {card.trial ? t("plans.trial") : planName(sub!.plan)}
      </span>

      <p data-plan-line className={cx("m-0 mt-1 text-lede font-medium", TONE_TEXT[card.tone])}>
        {line}
      </p>
      <CreditMeter value={card.left} max={card.total} tone={card.tone} label={line} />
      {card.date ? <p className="m-0 text-note text-ink-soft">{card.date}</p> : null}
      {pending ? <p className="m-0 text-note text-ink-soft">{pending}</p> : null}

      {card.extra > 0 ? <ExtraCredits extra={card.extra} note={t("plans.never_expire")} /> : null}
      {!card.trial && wallet.balance_credits < 0 ? <CreditsDue due={-wallet.balance_credits} /> : null}

      {stateLine ? (
        <p
          data-plan-state
          role={failed ? "alert" : undefined}
          className={cx("m-0 mt-1 text-note", failed ? "text-danger" : TONE_TEXT[card.tone])}
        >
          {stateLine}
        </p>
      ) : null}
      {wallet.blocked ? <p className="m-0 text-note text-danger">{t("billing.paused")}</p> : null}

      {card.trial ? (
        <Button className="mt-gutter" disabled={!wallet.checkout_available} onClick={() => onOpen("plans")}>
          {t("plans.see")}
        </Button>
      ) : failed ? (
        <Button
          className="mt-gutter"
          disabled={opening}
          onClick={() => {
            captureAnalytics("subscription_manage_opened", {});
            if (store) {
              captureAnalytics("link_opened", { link: "store_subscriptions" });
              window.open(STORE_SUBSCRIPTIONS_URL[store], "_blank", "noreferrer");
            } else {
              void portal();
            }
          }}
        >
          {t("plans.update_payment")}
        </Button>
      ) : short && wallet.checkout_available ? (
        <Button className="mt-gutter" onClick={() => onOpen("packs")}>
          {t("billing.add_time")}
        </Button>
      ) : null}
      {links ? <div className="flex flex-wrap items-center gap-x-pad gap-y-list mt-1">{links}</div> : null}
      {card.trial && !wallet.checkout_available ? (
        <p className="m-0 text-note text-ink-soft">{t("billing.unavailable")}</p>
      ) : null}
    </Card>
  );
}

/**
 * How much is left, as a bar in the card's tone: the accent, or the warn or error colour
 * with the line above it. A reading, not a control.
 */
function CreditMeter({ value, max, tone, label }: { value: number; max: number; tone: CardTone; label: string }) {
  const share = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.max(0, Math.min(max, value))}
      aria-label={label}
      data-credit-meter
      className="h-1.5 w-full overflow-hidden rounded-pill bg-surface-3"
    >
      <div
        className={cx("h-full rounded-pill", tone === "danger" ? "bg-danger" : tone === "warn" ? "bg-warn" : "bg-accent")}
        style={{ width: `${share * 100}%` }}
      />
    </div>
  );
}

/**
 * "{n} extra credits" with a note on the right: "Never expire" on the plan card, "Usable
 * with a plan" under No plan (#1028), where they can't start a meeting on their own.
 */
function ExtraCredits({ extra, note }: { extra: number; note: string }) {
  return (
    <div data-plan-extra className="flex items-baseline justify-between gap-stack mt-1 text-note">
      <span className="text-ink">{t("plans.extra_credits", { credits: credits(extra) })}</span>
      <span className="text-ink-soft text-right">{note}</span>
    </div>
  );
}

/** Below zero (#1028): "{n} credits due" in the error colour, on No plan's card and a plan's. */
function CreditsDue({ due }: { due: number }) {
  return (
    <p data-credits-due className="m-0 mt-1 text-note text-danger">
      {t("billing.short", { credits: credits(due) })}
    </p>
  );
}

/**
 * Without a plan (#1010, #1028): "No plan", that a plan is what continues meetings, the
 * credits left from packs bought before (the extra credits row, usable with a plan, or how
 * many short in the error colour below zero; nothing at zero), the paused note, and See
 * plans, the one offer. One card: the balance has no card of its own here.
 */
function NoPlanCard({ wallet, onOpen }: { wallet: Wallet; onOpen: (sheet: Sheet) => void }) {
  const balance = wallet.balance_credits;
  return (
    <Card data-plan-card data-billing-state={billingState(wallet)} className="flex flex-col gap-stack">
      <span className="font-title text-person font-semibold tracking-headline text-ink">{t("plans.none")}</span>
      <p className="m-0 text-note text-ink-soft">{t("plans.no_plan_continue")}</p>
      {balance > 0 ? <ExtraCredits extra={balance} note={t("plans.usable_with_plan")} /> : null}
      {balance < 0 ? <CreditsDue due={-balance} /> : null}
      {wallet.blocked && wallet.start_refusal !== null ? (
        <p className="m-0 text-note text-danger">{t("billing.paused")}</p>
      ) : null}
      <Button className="mt-gutter" disabled={!wallet.checkout_available} onClick={() => onOpen("plans")}>
        {t("plans.see")}
      </Button>
      {wallet.checkout_available ? null : (
        <p className="m-0 text-note text-ink-soft">{t("billing.unavailable")}</p>
      )}
    </Card>
  );
}

/**
 * The balance on its own (#686), with plans off the whole section: the credits left, due or
 * none, in the error colour when due, what a credit buys, and Add credits.
 */
function BalanceCard({ wallet, onTopUp }: { wallet: Wallet; onTopUp: () => void }) {
  const balance = wallet.balance_credits;
  const paused = wallet.blocked && wallet.start_refusal !== null;
  return (
    <Card data-balance-card className="flex flex-col gap-stack">
      <span
        className={cx(
          "block font-title text-stat font-bold tracking-headline",
          balance < 0 ? "text-danger" : "text-ink"
        )}
      >
        {balanceLine(balance)}
      </span>
      <p className="m-0 text-note leading-normal text-ink-soft">{t("billing.hint")}</p>
      {paused ? <p className="m-0 text-note text-danger">{t("billing.paused")}</p> : null}
      <Button className="mt-gutter" onClick={onTopUp} disabled={!wallet.checkout_available}>
        {t("billing.add_time")}
      </Button>
      {wallet.checkout_available ? null : (
        <p className="m-0 text-note text-ink-soft">{t("billing.unavailable")}</p>
      )}
    </Card>
  );
}

/**
 * The billing section and the history beneath it.
 *
 * With plans on (#1010) the plan card leads: the plan and its credits (`PlanCard`), or No
 * plan with any credits left and See plans (`NoPlanCard`, #1028). With plans
 * off, the balance card as before (#686). The figure is set like the record plate's title --
 * the one number this screen is for. The sentences under it follow the server's own
 * `start_refusal`, which is null while billing is not enforced: owing credits only says
 * "settle this first" when it actually stops the next meeting, and a paused account says
 * so plainly without suggesting a top-up lifts it, because it does not.
 */
function WalletSection({
  wallet,
  onOpen,
  onManageFailed,
}: {
  wallet: Wallet;
  onOpen: (sheet: Sheet) => void;
  onManageFailed: () => void;
}) {
  const navigate = useNavigate();
  return (
    <>
      {!plansOn(wallet) ? (
        <BalanceCard wallet={wallet} onTopUp={() => onOpen("packs")} />
      ) : (wallet.subscription && (wallet.plan || wallet.subscription.status === "on_hold")) ||
        (!wallet.plan && wallet.trial) ? (
        // A plan, or the free trial (#1024), which has the plan's card and no balance card.
        <PlanCard wallet={wallet} onOpen={onOpen} onManageFailed={onManageFailed} />
      ) : (
        <NoPlanCard wallet={wallet} onOpen={onOpen} />
      )}

      {/* The same act label, See all link and rows as home's recent meetings, so history
          reads as history wherever it appears -- without home's indent, which would put it
          off the left edge every heading on this screen shares. The latest few rows here;
          the link, only when there are more, opens the whole ledger. */}
      <div className="flex items-center justify-between my-6">
        <span className="font-code text-label font-bold uppercase tracking-label text-ink-soft">
          {t("billing.history")}
        </span>
        {wallet.transactions.length > HISTORY_PREVIEW ? (
          <Button variant="link" className="pr-1" onClick={() => navigate("/credits")}>
            {t("home.see_all")}
          </Button>
        ) : null}
      </div>
      {wallet.transactions.length === 0 ? (
        <p className="m-0 mb-4 text-note text-ink-soft">{t("billing.history_empty")}</p>
      ) : (
        <div className="grid gap-2 mb-4">
          {wallet.transactions.slice(0, HISTORY_PREVIEW).map((tx) => (
            <TransactionRow key={tx.id} tx={tx} />
          ))}
        </div>
      )}
    </>
  );
}
